/**
 * useMediaApi - Platform-Aware Media Operations
 *
 * Routes media operations to:
 * - Electron: Local PostgreSQL media tables
 * - Web/Cloud: HTTP API
 *
 * Follows the useArchiveApi pattern for stability:
 * - Uses refs for non-render state
 * - Callbacks read from refs at call time
 * - Memoized return object
 *
 * @module @humanizer/studio/hooks/useMediaApi
 */

import { useCallback, useMemo } from 'react';
import { usePlatformRouter, type Backend } from './usePlatformRouter';
import type { PlatformContextValue } from '../contexts/PlatformContext';
import type { ElectronMediaItem, ElectronMediaSearchResult } from '../types/electron';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Media item for listing */
export interface MediaItem {
  id: string;
  contentHash?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  width?: number;
  height?: number;
  duration?: number;
  sourcePointer?: string;
  sourceType?: string;
  nodeId?: string;
  caption?: string;
  transcript?: string;
  sourceCreatedAt?: string;
  createdAt?: string;
  type?: string;
  text?: string;
}

/** Media search result */
export interface MediaSearchResult {
  id: string;
  text?: string;
  type?: string;
  nodeTitle?: string;
  rank?: number;
  filename?: string;
}

/** Transcription version */
export interface TranscriptionVersion {
  id: string;
  versionNumber: number;
  type: string;
  status: string;
  text?: string;
  segments?: unknown[];
  model?: {
    id: string;
    provider: string;
    variant?: string;
  };
  isPreferred: boolean;
  completedAt?: string;
  quality?: {
    confidence?: number;
    language?: string;
    wordCount?: number;
  };
  processingDurationMs?: number;
}

/** Transcription result */
export interface TranscriptionResult {
  id: string;
  versionId?: string;
  text: string;
  type: string;
  model: string;
  status: string;
  processingDurationMs?: number;
  segments?: Array<{ start: number; end: number; text: string }>;
  language?: string;
  duration?: number;
}

export interface MediaApi {
  /** Whether media API is available */
  isAvailable: boolean;
  /** Which backend will be used */
  backend: Backend;
  /** Reason if unavailable */
  unavailableReason: string | null;

  /** List media items */
  list: (options?: {
    type?: 'image' | 'audio' | 'video' | 'all';
    sourceType?: string;
    limit?: number;
    offset?: number;
  }) => Promise<{
    items: MediaItem[];
    total: number;
    hasMore: boolean;
  }>;

  /** List distinct source types with counts */
  listSources: () => Promise<Array<{ sourceType: string; count: number }>>;

  /** Search media by text/caption */
  search: (query: string, limit?: number) => Promise<{
    results: MediaSearchResult[];
    total: number;
  }>;

  /** Get media item by pointer */
  byPointer: (pointer: string) => Promise<{
    found: boolean;
    media?: MediaItem;
  }>;

  /** Transcribe media (audio/video → text) */
  transcribe: (mediaId: string, type: string, archiveId?: string) => Promise<TranscriptionResult>;

  /** Get transcription versions for media */
  getTranscriptions: (mediaId: string, archiveId?: string) => Promise<{
    versions: TranscriptionVersion[];
  }>;

  /** Set preferred transcription version */
  setPreferredTranscription: (versionId: string) => Promise<{
    success: boolean;
    previousPreferredId?: string;
    newPreferredId: string;
  }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useMediaApi(): MediaApi {
  const { getBackend, isAvailable, backend, unavailableReason, cloudApi } = usePlatformRouter({
    resolveCapabilities(platform: PlatformContextValue | null, isElectron: boolean) {
      if (platform) {
        return {
          canUseLocal: platform.isElectron && platform.canUseLocalArchive,
          canUseCloud: platform.canUseCloudLLM || !platform.isElectron,
          preferredBackend: platform.isElectron && platform.canUseLocalArchive
            ? 'local' as const
            : (!platform.isElectron || platform.canUseCloudLLM ? 'cloud' as const : 'none' as const),
        };
      }
      return {
        canUseLocal: isElectron,
        canUseCloud: !isElectron,
        preferredBackend: isElectron ? 'local' as const : 'cloud' as const,
      };
    },
    resolveAvailability(platform: PlatformContextValue | null, isElectron: boolean) {
      const isAvailable = platform
        ? (platform.isElectron && platform.canUseLocalArchive) || !platform.isElectron
        : true;
      const backend = platform?.isElectron && platform?.canUseLocalArchive
        ? 'local' as const
        : (isElectron ? 'none' as const : 'cloud' as const);
      const unavailableReason = isAvailable
        ? null
        : 'PostgreSQL not configured. Configure database connection in settings.';
      return { isAvailable, backend, unavailableReason };
    },
    platformDeps: (platform) => [platform?.isElectron, platform?.canUseLocalArchive, platform?.canUseCloudLLM],
  });

  // ─────────────────────────────────────────────────────────────────────────
  // MEDIA OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────

  const list = useCallback(async (options?: {
    type?: 'image' | 'audio' | 'video' | 'all';
    sourceType?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: MediaItem[]; total: number; hasMore: boolean }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.listMedia) {
        throw new Error('Local media API not available');
      }
      const result = await electronArchive.listMedia(options);
      return {
        items: result.items.map((m: ElectronMediaItem) => ({
          id: m.id,
          sourcePointer: m.sourcePointer,
          mimeType: m.mimeType,
          type: m.type,
          text: m.text,
          caption: m.caption || m.text,
          filename: m.filename,
          size: m.size,
          width: m.width,
          height: m.height,
          duration: m.duration,
          sourceType: m.sourceType,
          sourceCreatedAt: m.sourceCreatedAt,
          createdAt: m.createdAt,
        })),
        total: result.total,
        hasMore: result.hasMore,
      };
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.media.list(options);
      return {
        items: result.items.map((m) => ({
          ...m,
          filename: m.filename ?? undefined,
          size: m.size ?? undefined,
          width: m.width ?? undefined,
          height: m.height ?? undefined,
          duration: m.duration ?? undefined,
          sourcePointer: m.sourcePointer ?? undefined,
          sourceType: m.sourceType ?? undefined,
          nodeId: m.nodeId ?? undefined,
          caption: m.caption ?? undefined,
          transcript: m.transcript ?? undefined,
          sourceCreatedAt: m.sourceCreatedAt ?? undefined,
        })),
        total: result.total,
        hasMore: result.hasMore,
      };
    }

    throw new Error(unavailableReason || 'No media backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const listSources = useCallback(async (): Promise<Array<{ sourceType: string; count: number }>> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.listMediaSources) {
        return [];
      }
      return electronArchive.listMediaSources();
    }

    if (currentBackend === 'cloud') {
      // Cloud doesn't have listSources yet — return empty
      return [];
    }

    return [];
  }, [getBackend]);

  const search = useCallback(async (
    query: string,
    limit?: number
  ): Promise<{ results: MediaSearchResult[]; total: number }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.searchMedia) {
        throw new Error('Local media search not available');
      }
      const result = await electronArchive.searchMedia(query, limit);
      return {
        results: result.results.map((r: ElectronMediaSearchResult) => ({
          id: r.id,
          text: r.text,
          type: r.type,
          nodeTitle: r.nodeTitle,
          rank: r.rank,
        })),
        total: result.total,
      };
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.media.search(query, limit);
      return {
        results: result.results.map((r) => ({
          id: r.id,
          text: r.caption ?? r.transcript ?? undefined,
          type: r.mimeType?.split('/')[0] ?? undefined,
          nodeTitle: undefined,
          rank: undefined,
          filename: r.filename ?? undefined,
        })),
        total: result.total,
      };
    }

    throw new Error(unavailableReason || 'No media backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const byPointer = useCallback(async (pointer: string): Promise<{
    found: boolean;
    media?: MediaItem;
  }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronArchive = window.electronArchive;
      if (!electronArchive?.getMediaByPointer) {
        return { found: false };
      }
      const result = await electronArchive.getMediaByPointer(pointer);
      if (!result.found || !result.media) {
        return { found: false };
      }
      return {
        found: true,
        media: {
          id: result.media.id,
          sourcePointer: result.media.sourcePointer,
          mimeType: result.media.mimeType,
          filename: result.media.filename,
          size: result.media.size,
          width: result.media.width,
          height: result.media.height,
          duration: result.media.duration,
          sourceType: result.media.sourceType,
          caption: result.media.caption,
          sourceCreatedAt: result.media.sourceCreatedAt,
          createdAt: result.media.createdAt,
        },
      };
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.media.byPointer(pointer);
      if (!result.found || !result.media) {
        return { found: false };
      }
      return {
        found: true,
        media: {
          ...result.media,
          filename: result.media.filename ?? undefined,
          size: result.media.size ?? undefined,
          width: result.media.width ?? undefined,
          height: result.media.height ?? undefined,
          duration: result.media.duration ?? undefined,
          sourcePointer: result.media.sourcePointer ?? undefined,
          sourceType: result.media.sourceType ?? undefined,
          nodeId: result.media.nodeId ?? undefined,
          caption: result.media.caption ?? undefined,
          transcript: result.media.transcript ?? undefined,
          sourceCreatedAt: result.media.sourceCreatedAt ?? undefined,
        },
      };
    }

    throw new Error(unavailableReason || 'No media backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const transcribe = useCallback(async (
    mediaId: string,
    type: string,
    archiveId?: string
  ): Promise<TranscriptionResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      if (!window.electronArchive?.transcribeMedia) {
        throw new Error('Transcription not available in this version.');
      }
      return window.electronArchive.transcribeMedia(mediaId, type);
    }

    if (currentBackend === 'cloud') {
      return cloudApi.media.transcribe(mediaId, type, archiveId);
    }

    throw new Error(unavailableReason || 'No transcription backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const getTranscriptions = useCallback(async (
    mediaId: string,
    archiveId?: string
  ): Promise<{ versions: TranscriptionVersion[] }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      if (!window.electronArchive?.getTranscriptions) {
        return { versions: [] };
      }
      return window.electronArchive.getTranscriptions(mediaId);
    }

    if (currentBackend === 'cloud') {
      return cloudApi.media.getTranscriptions(mediaId, archiveId);
    }

    throw new Error(unavailableReason || 'No transcription backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const setPreferredTranscription = useCallback(async (versionId: string): Promise<{
    success: boolean;
    previousPreferredId?: string;
    newPreferredId: string;
  }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      return window.electronArchive!.setPreferredTranscription(versionId);
    }

    if (currentBackend === 'cloud') {
      return cloudApi.media.setPreferredTranscription(versionId);
    }

    throw new Error(unavailableReason || 'No transcription backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  // Memoize the returned object for stability
  return useMemo(() => ({
    isAvailable,
    backend,
    unavailableReason,
    list,
    listSources,
    search,
    byPointer,
    transcribe,
    getTranscriptions,
    setPreferredTranscription,
  }), [
    isAvailable,
    backend,
    unavailableReason,
    list,
    listSources,
    search,
    byPointer,
    transcribe,
    getTranscriptions,
    setPreferredTranscription,
  ]);
}

export default useMediaApi;
